import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRightIcon } from '@/components/icons/icon-set'
import { Seo } from '@/components/generals/seo'
import { ChecklistItem } from '@/components/ui/checklist-item'
import { CodeBlock } from '@/components/docs/code-block'
import { DocsLayout, DocsPageHeader, DocsPager, DocsSection } from '@/components/docs/docs-layout'
import { DOCS_NAV } from '@/constants/docs-nav'

const TOC = [
    { id: 'principles', title: 'Principles' },
    { id: 'structure', title: 'How it is organized' },
    { id: 'building', title: 'Building a screen' },
    { id: 'your-rules', title: 'Your rules' }
]

/* Get started / Introduction */
const DocsIntroduction: FC = (): ReactNode => {
    return (
        <DocsLayout toc={TOC}>
            <Seo title='Introduction' description='What the GoodUnited design system is, how it is organized and how to build with it.' />

            <DocsPageHeader
                title='Introduction'
                description='The GoodUnited design system turns the Figma file into working code. It follows shadcn for naming and structure, and GoodUnited for every color, type style, size and radius.'
                group='Get started'
                href='/docs'
            />

            <DocsSection id='principles' title='Principles'>
                <div className='flex flex-col gap-1 rounded-xl border border-border-subtle bg-surface-raised p-6'>
                    <ChecklistItem state='complete'>The Figma file is the source of truth for styling. shadcn shapes structure and naming only.</ChecklistItem>
                    <ChecklistItem state='complete'>Semantic tokens only: bg-surface-raised, text-text-secondary, border-border-subtle. Never raw colors.</ChecklistItem>
                    <ChecklistItem state='complete'>Only the twelve text styles: Poppins for headings and key metrics, Roboto for everything else.</ChecklistItem>
                    <ChecklistItem state='complete'>Screens are composed from these components, blocks and templates before anything new is made.</ChecklistItem>
                    <ChecklistItem state='complete'>Every screen works in light and dark, on phones, tablets and desktops.</ChecklistItem>
                </div>
            </DocsSection>

            <DocsSection id='structure' title='How it is organized' description='The sidebar follows the Figma page order.'>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    {DOCS_NAV.filter(group => group.title !== 'Get started').map(group => (
                        <div key={group.title} className='flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-raised p-5'>
                            <p className='font-heading text-lg font-semibold text-text-primary'>
                                {group.title}
                            </p>
                            <p className='text-sm text-text-secondary'>
                                {group.items.length === 1 ? group.items[0].description : `${group.items.length} pages: ${group.items.slice(0, 5).map(item => item.title).join(', ')}${group.items.length > 5 ? ' and more' : ''}.`}
                            </p>
                            <Link href={group.items[0].href} className='inline-flex w-fit items-center gap-1 rounded-sm text-sm font-medium text-text-link outline-none hover:text-text-link-hover focus-visible:outline-2 focus-visible:outline-border-focus'>
                                {`Open ${group.title}`}
                                <ArrowRightIcon className='size-4' aria-hidden='true' />
                            </Link>
                        </div>
                    ))}
                </div>
            </DocsSection>

            <DocsSection id='building' title='Building a screen' description='Start from a template, keep the shell, and fill its containers with components and blocks.'>
                <CodeBlock
                    title='src/pages/inbox.tsx'
                    code={`import { AppShell, PageBody } from '@/components/blocks/app-layout'\nimport { KanbanBoard } from '@/components/blocks/kanban'\n\nexport default function Inbox() {\n    return (\n        <AppShell title='Inbox' activeItem='actions' showDate>\n            <PageBody title='Inbox' description='AI-recommended actions, reviewed and approved by your GoodUnited team'>\n                <KanbanBoard />\n            </PageBody>\n        </AppShell>\n    )\n}`}
                />
            </DocsSection>

            <DocsSection id='your-rules' title='Your rules' description='The Settings page shows the project guide every build follows, and lets you add your own rules, such as writing style or when to use drawers.'>
                <Link href='/settings' className='inline-flex w-fit items-center gap-1 rounded-sm text-base font-medium text-text-link outline-none hover:text-text-link-hover focus-visible:outline-2 focus-visible:outline-border-focus'>
                    Open Settings
                    <ArrowRightIcon className='size-4' aria-hidden='true' />
                </Link>
            </DocsSection>

            <DocsPager href='/docs' />
        </DocsLayout>
    )
}

export default DocsIntroduction
